<template>
  <div
    data-testid="data-configuration-column-mapping-panel"
    class="data-configuration-column-mapping-panel"
  >
    <div class="data-configuration-column-mapping-panel__header">
      <div class="data-configuration-column-mapping-panel__header-text">
        <div class="data-configuration-column-mapping-panel__title-row">
          <h2
            data-testid="data-configuration-column-mapping-panel__title"
            class="data-configuration-column-mapping-panel__title"
          >
            {{ sectionTitle }}
          </h2>
          <div
            v-if="hasSourceFile"
            class="data-configuration-column-mapping-panel__ingest"
            data-testid="data-configuration-column-mapping-panel__mapping-status"
          >
            <v-icon small :color="mappingPending ? 'warning' : 'success'">
              {{ mappingPending ? 'mdi-alert-circle-outline' : 'mdi-check-circle' }}
            </v-icon>
            <span>{{ mappingStatusLabel }}</span>
          </div>
          <div
            v-if="hasSourceFile"
            class="data-configuration-column-mapping-panel__ingest"
            data-testid="data-configuration-column-mapping-panel__ingest-status"
          >
            <template v-if="!ingestionStatusLoaded">
              <v-progress-circular
                indeterminate
                size="14"
                width="2"
                color="primary"
              />
              <span>Loading status…</span>
            </template>
            <template v-else-if="ingestionComplete">
              <v-icon small color="success">mdi-check-circle</v-icon>
              <span>Last updated {{ ingestionUpdatedLabel }}</span>
            </template>
            <template v-else-if="showIngestProgress">
              <div
                class="data-configuration-column-mapping-panel__ingest-bar"
                role="progressbar"
                :aria-valuenow="ingestionProgress"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-label="`Ingestion progress ${ingestionProgress}%`"
              >
                <div
                  class="data-configuration-column-mapping-panel__ingest-bar-fill"
                  :style="{ width: `${ingestionProgress}%` }"
                />
              </div>
              <span>{{ ingestionProgress }}%</span>
            </template>
            <template v-else-if="ingestionFailed">
              <v-icon small color="error">mdi-alert-circle</v-icon>
              <span>Ingestion failed</span>
            </template>
            <template v-else>
              <v-icon small color="grey">mdi-progress-clock</v-icon>
              <span>Not ingested yet</span>
            </template>
          </div>
        </div>
      </div>

      <div class="data-configuration-column-mapping-panel__header-actions">
        <template v-if="isEditMode">
          <base-button
            secondary
            small
            class="shadow-off"
            label="Cancel"
            :disabled="busy"
            data-testid="data-configuration-column-mapping-panel__cancel"
            @click="cancelEdit"
          />
          <base-button
            primary
            small
            class="shadow-off"
            label="Save"
            :disabled="!canSave"
            :loading="syncingColumns"
            data-testid="data-configuration-column-mapping-panel__save"
            @click="saveMapping"
          />
        </template>

        <base-button
          v-if="isViewMode && canMutateMapping"
          secondary
          small
          class="shadow-off"
          label="Sync columns via file upload"
          :disabled="loading"
          data-testid="data-configuration-column-mapping-panel__sync"
          @click="syncModalOpen = true"
        />
        <base-button
          v-if="isViewMode && canMutateMapping"
          primary
          small
          class="shadow-off"
          label="Ingest now"
          :disabled="!canIngestNow"
          :loading="startingIngestion"
          data-testid="data-configuration-column-mapping-panel__ingest-now"
          @click="ingestNow"
        />

        <base-button
          v-if="isViewMode && canMutateMapping"
          secondary
          small
          class="shadow-off"
          label="Edit"
          :disabled="loading || !hasSourceFile"
          data-testid="data-configuration-column-mapping-panel__edit"
          @click="startEdit"
        />
      </div>
    </div>

    <v-skeleton-loader
      v-if="loading"
      type="table-heading, table-row-divider@6"
      class="data-configuration-column-mapping-panel__skeleton"
      data-testid="data-configuration-column-mapping-panel__skeleton"
    />

    <template v-else-if="loadError">
      <div
        class="data-configuration-column-mapping-panel__load-error"
        data-testid="data-configuration-column-mapping-panel__load-error"
      >
        <v-icon color="error">mdi-alert-circle-outline</v-icon>
        <p class="data-configuration-column-mapping-panel__load-error-text">
          Couldn't load column mapping. Check your connection and try again.
        </p>
        <base-button
          secondary
          small
          class="shadow-off"
          label="Retry"
          data-testid="data-configuration-column-mapping-panel__retry"
          @click="loadSectionData"
        />
      </div>
    </template>

    <template v-else>
      <p
        v-if="!canonicalFields.length"
        class="data-configuration-column-mapping-panel__field-hint"
        data-testid="data-configuration-column-mapping-panel__missing-canonical"
      >
        Canonical fields are not set up for this client. Seed
        accruals_canonical_field_master before mapping columns.
      </p>
      <p
        v-else-if="!sourceHeaders.length"
        class="data-configuration-column-mapping-panel__field-hint"
        data-testid="data-configuration-column-mapping-panel__missing-source"
      >
        No ERP file has been uploaded for this dataset. Source column choices
        appear after a file is uploaded.
      </p>
      <div
        data-testid="data-configuration-column-mapping-panel__table-wrapper"
        class="data-configuration-column-mapping-panel__table"
      >
        <lb-datatableaction
          :headers="mappingTableHeaders"
          :tabledata="mappingRows"
          :displayed-row-count="mappingRows.length"
          :bulk-actions="false"
          :show-toolbar="false"
          :enable-column-reorder="false"
          :enable-cell-truncate="false"
          header-bg-class="fbc-table-header-bg grayBackground"
          data-testid="data-configuration-column-mapping-panel__datatable"
        >
          <template #tbody>
            <tr
              v-for="(row, index) in mappingRows"
              :key="row.rowKey || row.destination || index"
              class="data-configuration-column-mapping-panel__row"
              :class="{ 'data-configuration-column-mapping-panel__row--custom': row.isExtra }"
              :data-testid="`data-configuration-column-mapping-panel__row--${index}`"
            >
              <template v-for="header in mappingTableHeaders">
                <td
                  v-if="header.visible && header.value === 'destination'"
                  :key="`${header.value}-${index}-destination`"
                  class="data-configuration-column-mapping-panel__cell data-configuration-column-mapping-panel__cell--destination"
                >
                  <span class="data-configuration-column-mapping-panel__db-value">
                    {{ row.destination }}
                  </span>
                </td>

                <td
                  v-else-if="header.visible && header.value === 'dataType'"
                  :key="`${header.value}-${index}-data-type`"
                  class="data-configuration-column-mapping-panel__cell data-configuration-column-mapping-panel__cell--data-type"
                >
                  <span class="data-configuration-column-mapping-panel__type-value">
                    {{ rowDataType(row) }}
                  </span>
                </td>

                <td
                  v-else-if="header.visible && header.value === 'arrow'"
                  :key="`${header.value}-${index}-arrow`"
                  class="data-configuration-column-mapping-panel__cell data-configuration-column-mapping-panel__cell--arrow"
                >
                  <img
                    src="/img/accruals/ArrowRight.svg"
                    alt=""
                    class="data-configuration-column-mapping-panel__arrow-icon"
                    aria-hidden="true"
                  />
                </td>

                <td
                  v-else-if="header.visible && header.value === 'source'"
                  :key="`${header.value}-${index}-source`"
                  class="data-configuration-column-mapping-panel__cell data-configuration-column-mapping-panel__cell--source"
                >
                  <div class="data-configuration-column-mapping-panel__erp-cell">
                    <data-configuration-erp-column-dropdown
                      v-model="row.source"
                      :destination="row.destination"
                      :items="erpColumnItems"
                      :suggested-source="getSuggestedSourceForRow(row.destination)"
                      :disabled="busy || (row.isExtra && !row.destination)"
                      :readonly="!isEditMode"
                      :data-testid="`data-configuration-column-mapping-panel__erp-select--${index}`"
                      @change="markMappingDirty"
                    />
                  </div>
                </td>

                <td
                  v-else-if="header.visible && header.value === 'mask'"
                  :key="`${header.value}-${index}-mask`"
                  class="data-configuration-column-mapping-panel__cell data-configuration-column-mapping-panel__cell--mask"
                >
                  <div class="data-configuration-column-mapping-panel__mask-cell">
                    <lb-dropdown
                      :value="row.mask || maskNone"
                      :items="maskItems"
                      itemtext="label"
                      itemvalue="value"
                      label=""
                      hidedetails
                      :searchable="false"
                      :clearable="false"
                      :disabled="busy"
                      :readonly="!isEditMode"
                      appendiconinner="mdi-menu-down"
                      class="data-configuration-column-mapping-panel__mask-dropdown"
                      :class="{
                        'data-configuration-column-mapping-panel__mask-dropdown--locked': !isEditMode,
                      }"
                      :data-testid="`data-configuration-column-mapping-panel__mask-select--${index}`"
                      @input="onMaskChange(row, $event)"
                    />
                  </div>
                </td>

                <td
                  v-else-if="header.visible && header.value === 'requirement'"
                  :key="`${header.value}-${index}-requirement`"
                  class="data-configuration-column-mapping-panel__cell data-configuration-column-mapping-panel__cell--requirement"
                >
                  <div class="data-configuration-column-mapping-panel__requirement-badges">
                    <span
                      v-if="row.isExtra"
                      class="data-configuration-column-mapping-panel__badge data-configuration-column-mapping-panel__badge--custom"
                    >
                      Custom
                    </span>
                    <span
                      class="data-configuration-column-mapping-panel__badge"
                      :class="row.required
                        ? 'data-configuration-column-mapping-panel__badge--required'
                        : 'data-configuration-column-mapping-panel__badge--optional'"
                    >
                      {{ row.required ? 'Required' : 'Optional' }}
                    </span>
                    <v-btn
                      v-if="isEditMode && row.isExtra"
                      icon
                      x-small
                      class="data-configuration-column-mapping-panel__edit-extra-btn"
                      :disabled="busy"
                      :data-testid="`data-configuration-column-mapping-panel__edit-extra-${index}`"
                      @click="editExtraColumn(row)"
                    >
                      <img src="/img/icons/fbc-icons/PencilEdit01.svg" alt="Edit custom column" aria-hidden="true">
                    </v-btn>
                    <v-btn
                      v-if="isEditMode && row.isExtra"
                      icon
                      x-small
                      class="data-configuration-column-mapping-panel__remove-extra-btn"
                      :disabled="busy"
                      :data-testid="`data-configuration-column-mapping-panel__remove-extra-${index}`"
                      @click="removeExtraColumn(index)"
                    >
                      <img src="/img/icons/fbc-icons/DeleteActions.svg" alt="" aria-hidden="true">
                    </v-btn>
                    <v-btn
                      v-if="isEditMode && !row.isExtra && !row.required"
                      icon
                      x-small
                      class="data-configuration-column-mapping-panel__edit-extra-btn"
                      :disabled="busy"
                      :data-testid="`data-configuration-column-mapping-panel__edit-column-${index}`"
                      @click="editCanonicalColumn(row)"
                    >
                      <img src="/img/icons/fbc-icons/PencilEdit01.svg" alt="Edit column mapping" aria-hidden="true">
                    </v-btn>
                    <v-btn
                      v-if="isEditMode && !row.isExtra && !row.required && row.source"
                      icon
                      x-small
                      class="data-configuration-column-mapping-panel__unmap-btn"
                      :disabled="busy"
                      :data-testid="`data-configuration-column-mapping-panel__unmap-${index}`"
                      @click="unmapCanonicalColumn(row)"
                    >
                      <img src="/img/icons/fbc-icons/DeleteActions.svg" alt="Remove column mapping" aria-hidden="true">
                    </v-btn>
                  </div>
                </td>
              </template>
            </tr>
          </template>
        </lb-datatableaction>
      </div>

      <base-button
        v-if="isEditMode"
        text
        small
        prepend-icon="mdi-plus"
        class="shadow-off data-configuration-column-mapping-panel__add-column-link"
        label="Add Custom Column"
        :disabled="busy"
        data-testid="data-configuration-column-mapping-panel__add-column"
        @click="showCustomColumnDialog = true"
      />
    </template>

    <accruals-sync-upload-modal
      v-model="syncModalOpen"
      :entity-type="entityType"
      project-id=""
      :cid="cid"
    />

    <data-configuration-custom-column-dialog
      v-model="showCustomColumnDialog"
      :erp-column-items="erpColumnItems"
      :edit-payload="customColumnEditPayload"
      data-testid="data-configuration-column-mapping-panel__custom-column-dialog"
      @create="onCustomColumnCreate"
      @save="onCustomColumnSave"
    />
  </div>
</template>

<script>
import AccrualsSyncUploadModal from '@/modules/financialclose/views/Automation/Accruals/components/AccrualsSyncUploadModal.vue';
import DataConfigurationCustomColumnDialog from './DataConfigurationCustomColumnDialog.vue';
import DataConfigurationErpColumnDropdown from './DataConfigurationErpColumnDropdown.vue';
import { isAppSwitcherManagerUser } from '@/components/navigation/MainNav/mainNavUtils';
import {
  COLUMN_MASK,
  COLUMN_MASK_ITEMS,
  buildErpColumnItems,
  cloneMappingRows,
  inferDataType,
  normalizeColumnMask,
  normalizeDropdownValue,
  slugifyColumnName,
} from '@/modules/admin/utils/dataConfigurationUtils';

const SOURCE_HEADER_META_KEYS = new Set([
  '_id',
  'project_id',
  'uploaded_at',
  '__source_headers',
  'ingestionRunId',
  's3_chunk_key',
  'created_at',
  'batch_index',
  'version_id',
]);

// Hang guard: store dispatches have no timeout, so a dead backend would leave
// the skeleton loader on screen forever. The race below surfaces an error
// state with a retry instead.
const SECTION_LOAD_TIMEOUT_MS = 20000;

export default {
  name: 'DataConfigurationColumnMappingPanel',
  components: {
    AccrualsSyncUploadModal,
    DataConfigurationCustomColumnDialog,
    DataConfigurationErpColumnDropdown,
  },
  props: {
    tab: {
      type: Object,
      required: true,
    },
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      mode: 'view',
      resolvedProjectId: '',
      loading: true,
      loadError: false,
      mappingRows: [],
      savedRows: [],
      sourceHeaders: [],
      headerLabels: {},
      canonicalFields: [],
      suggestedMappings: [],
      syncingColumns: false,
      mappingDirty: false,
      mappingSaved: false,
      showCustomColumnDialog: false,
      editingRowKey: null,
      nextExtraRowKeyCounter: 1,
      maskItems: COLUMN_MASK_ITEMS,
      maskNone: COLUMN_MASK.NONE,
      ingestion: null,
      ingestionStatusLoaded: false,
      ingestionTimer: null,
      syncModalOpen: false,
      startingIngestion: false,
      ingestPolling: false,
    };
  },
  computed: {
    customColumnEditPayload() {
      const row = this.findRowByEditKey(this.editingRowKey);
      if (!row) return null;
      return {
        rowKey: row.isExtra ? row.rowKey : row.destination,
        columnName: row.destination,
        displayLabel: row.displayLabel || '',
        dataType: row.dataType,
        erpColumn: row.source,
        mask: row.mask,
        required: row.required,
        isCanonical: !row.isExtra,
      };
    },
    entityType() {
      return this.tab?.entityType || 'open_po';
    },
    sectionTitle() {
      return `${this.tab?.label || ''} Column Mapping`;
    },
    projectId() {
      return (
        this.resolvedProjectId ||
        this.$route.query.project ||
        this.$route.query.projectId ||
        this.$route.params.id ||
        ''
      );
    },
    cid() {
      const client = this.$store.state.client;
      if (client && typeof client === 'object') {
        return client._id || client.id || this.$route.query.cid || '';
      }
      return client || this.$route.query.cid || '';
    },
    busy() {
      return this.loading || this.syncingColumns;
    },
    isViewMode() {
      return this.mode === 'view';
    },
    isEditMode() {
      return this.mode === 'edit';
    },
    erpColumnItems() {
      return buildErpColumnItems(this.filteredSourceHeaders, this.headerLabels);
    },
    filteredSourceHeaders() {
      return (this.sourceHeaders || []).filter(
        (slug) => slug && !SOURCE_HEADER_META_KEYS.has(String(slug))
      );
    },
    hasSourceFile() {
      return this.filteredSourceHeaders.length > 0;
    },
    mappingComplete() {
      const required = (this.canonicalFields || []).filter((field) => field.required);
      if (!required.length) {
        return this.mappingRows.some((row) => !row.isExtra && row.source);
      }
      return required.every((field) => {
        const row = this.mappingRows.find(
          (item) => !item.isExtra && item.destination === field.destination
        );
        return row && row.source;
      });
    },
    canMutateMapping() {
      return isAppSwitcherManagerUser(this.$store);
    },
    canSave() {
      return this.canMutateMapping
        && this.mappingComplete
        && !this.busy
        && (this.mappingDirty || !this.mappingSaved);
    },
    mappingPending() {
      return !this.mappingSaved || this.mappingDirty || !this.mappingComplete;
    },
    mappingStatusLabel() {
      return this.mappingPending ? 'Awaiting column mapping' : 'Column mapping saved';
    },
    canIngestNow() {
      return (
        this.canMutateMapping &&
        this.mappingSaved &&
        !this.mappingDirty &&
        this.mappingComplete &&
        !this.loading &&
        !this.ingestionRunning &&
        !this.startingIngestion &&
        !this.ingestPolling
      );
    },
    ingestionStatus() {
      return this.ingestion?.status || 'not_started';
    },
    ingestionComplete() {
      return this.ingestionStatus === 'completed';
    },
    ingestionRunning() {
      return this.ingestionStatus === 'in-progress' || this.ingestionStatus === 'in_progress';
    },
    showIngestProgress() {
      return this.ingestionRunning || this.ingestPolling;
    },
    ingestionFailed() {
      return this.ingestionStatus === 'failed' || this.ingestionStatus === 'cancelled';
    },
    ingestionProgress() {
      const value = Number(this.ingestion?.progress);
      if (Number.isNaN(value)) return 0;
      return Math.max(0, Math.min(100, Math.round(value)));
    },
    ingestionUpdatedLabel() {
      const value = this.ingestion?.updated_at;
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    mappingTableHeaders() {
      const headers = [
        {
          text: 'DB columns',
          value: 'destination',
          visible: true,
          sortable: false,
          filterable: false,
        },
        {
          text: 'Data type',
          value: 'dataType',
          visible: true,
          sortable: false,
          filterable: false,
          width: '100px',
          headerClass: 'data-configuration-column-mapping-panel__header-text--muted',
        },
        {
          text: '',
          value: 'arrow',
          visible: true,
          sortable: false,
          filterable: false,
          width: '24px',
        },
        {
          text: 'ERP source columns',
          value: 'source',
          visible: true,
          sortable: false,
          filterable: false,
          width: '230px',
        },
        {
          text: 'Policy',
          value: 'mask',
          visible: true,
          sortable: false,
          filterable: false,
          width: '120px',
        },
        {
          text: 'Requirement',
          value: 'requirement',
          visible: true,
          sortable: false,
          filterable: false,
          width: '120px',
        },
      ];

      return headers;
    },
  },
  watch: {
    showCustomColumnDialog(open) {
      if (!open) this.editingRowKey = null;
    },
    active(isActive) {
      if (isActive) {
        this.loadSectionData();
      }
    },
    syncModalOpen(open, wasOpen) {
      if (wasOpen && !open && this.active) {
        this.loadSectionData();
      }
    },
    entityType(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.mode = 'view';
        if (this.active) {
          this.loadSectionData();
        }
      }
    },
    cid() {
      if (this.active) {
        this.loadSectionData();
      }
    },
  },
  mounted() {
    this.loadManagerCycles();
    if (this.active) {
      this.loadSectionData();
    }
  },
  beforeDestroy() {
    this.stopIngestionPolling();
  },
  methods: {
    rowDataType(row) {
      if (row?.dataType) return row.dataType;
      return inferDataType(row?.destination);
    },
    getSuggestedSourceForRow(destination) {
      const match = (this.suggestedMappings || []).find(
        (item) => item?.destination === destination
      );
      return match?.source || '';
    },
    onMaskChange(row, value) {
      if (!this.isEditMode || this.busy || !row) return;
      row.mask = normalizeColumnMask(value);
      this.markMappingDirty();
    },
    async resolveProjectId() {
      const fromRoute =
        this.$route.query.project ||
        this.$route.query.projectId ||
        this.$route.params.id ||
        '';
      if (fromRoute) {
        this.resolvedProjectId = String(fromRoute);
        return this.resolvedProjectId;
      }

      try {
        const mapped = await this.$store.dispatch('accruals/loadMappingProject', {
          cid: this.cid,
          entityType: this.entityType,
        });
        if (mapped?.project_id) {
          this.resolvedProjectId = String(mapped.project_id);
          return this.resolvedProjectId;
        }
      } catch (err) {
        console.log('[DataConfigurationColumnMappingPanel]: resolveProjectId', err);
      }

      const cachedCycles = this.$store.state.m_financialclose?.cyclesForSwitcher;
      if (Array.isArray(cachedCycles) && cachedCycles.length) {
        this.resolvedProjectId = String(cachedCycles[0]._id || '');
        return this.resolvedProjectId;
      }

      try {
        const cycles = await this.$store.dispatch('fetchCyclesForSwitcher');
        if (Array.isArray(cycles) && cycles.length) {
          this.resolvedProjectId = String(cycles[0]._id || '');
          return this.resolvedProjectId;
        }
      } catch (err) {
        console.log('[DataConfigurationColumnMappingPanel]: resolveProjectId cycles', err);
      }

      this.resolvedProjectId = '';
      return '';
    },
    async refreshIngestionStatus() {
      if (!this.cid) {
        this.ingestionStatusLoaded = true;
        return;
      }
      try {
        const data = await this.$store.dispatch('accruals/loadMappingFlowStatus', { cid: this.cid });
        const next = data?.entities?.[this.entityType] || null;
        const status = next?.status || 'not_started';
        const stillWaiting =
          this.ingestPolling && (status === 'not_started' || !next);
        if (!stillWaiting) {
          this.ingestion = next;
        }
        if (status === 'completed' || status === 'failed' || status === 'cancelled') {
          this.ingestPolling = false;
        }
      } catch (err) {
        console.log('[DataConfigurationColumnMappingPanel]: ingestion status', err);
      }
      this.ingestionStatusLoaded = true;
      this.syncIngestionPolling();
    },
    syncIngestionPolling() {
      if (!this.ingestionRunning && !this.ingestPolling) {
        this.stopIngestionPolling();
        return;
      }
      if (this.ingestionTimer) return;
      this.ingestionTimer = setInterval(() => {
        this.refreshIngestionStatus();
      }, 4000);
    },
    stopIngestionPolling() {
      if (!this.ingestionTimer) return;
      clearInterval(this.ingestionTimer);
      this.ingestionTimer = null;
    },
    async loadSectionData() {
      this.loading = true;
      this.loadError = false;
      this.ingestionStatusLoaded = false;
      this.stopIngestionPolling();
      this.ingestion = null;
      let loadTimer = null;
      try {
        await Promise.race([
          this.fetchSectionData(),
          new Promise((_, reject) => {
            loadTimer = setTimeout(() => {
              reject(new Error('Data configuration section load timed out'));
            }, SECTION_LOAD_TIMEOUT_MS);
          }),
        ]);
      } catch (err) {
        console.log('[DataConfigurationColumnMappingPanel]: loadSectionData', err);
        this.loadError = true;
      } finally {
        if (loadTimer) {
          clearTimeout(loadTimer);
          loadTimer = null;
        }
        this.loading = false;
        if (!this.loadError) {
          this.refreshIngestionStatus();
        } else {
          this.ingestionStatusLoaded = true;
        }
      }
    },
    async fetchSectionData() {
      const projectId = await this.resolveProjectId();
        let canonical = null;
        try {
          canonical = await this.$store.dispatch('accruals/loadCanonicalFields', {
            cid: this.cid,
            entityType: this.entityType,
          });
        } catch (err) {
          console.log('[DataConfigurationColumnMappingPanel]: loadCanonicalFields', err);
        }

        let headers = null;
        try {
          headers = await this.$store.dispatch('accruals/loadSourceHeaders', {
            projectId: projectId || '',
            cid: this.cid,
            entityType: this.entityType,
          });
        } catch (err) {
          console.log('[DataConfigurationColumnMappingPanel]: loadSourceHeaders', err);
        }

        let normalization = null;
        try {
          normalization = await this.$store.dispatch('accruals/loadNormalizationConfig', {
            projectId: projectId || '',
            cid: this.cid,
            entityType: this.entityType,
          });
        } catch (err) {
          console.log('[DataConfigurationColumnMappingPanel]: loadNormalizationConfig', err);
        }

        this.applyUploadPayload({
          ...(headers || {}),
          canonical_fields:
            normalization?.canonical_fields ||
            headers?.canonical_fields ||
            canonical?.fields ||
            [],
        });

        if (normalization) {
          this.syncMappingFromNormalization(normalization);
          this.mappingSaved = !!(normalization.configured && normalization.mapping_complete);
        } else {
          this.savedRows = cloneMappingRows(this.mappingRows);
          this.mappingDirty = false;
          this.mappingSaved = false;
        }
    },
    resetMappingState() {
      this.mappingRows = [];
      this.savedRows = [];
      this.sourceHeaders = [];
      this.headerLabels = {};
      this.canonicalFields = [];
      this.suggestedMappings = [];
      this.syncingColumns = false;
      this.mappingDirty = false;
      this.mappingSaved = false;
      this.mode = 'view';
    },
    applyUploadPayload(data) {
      this.sourceHeaders = this.filterSourceHeaders(
        data?.source_headers || data?.detected_source_headers || []
      );
      this.headerLabels = data?.header_labels || {};
      this.suggestedMappings = data?.suggested_mappings || [];
      const canonical = data?.canonical_fields || [];
      if (canonical.length) {
        this.canonicalFields = canonical;
      }
      const sourceByDest = {};
      const maskByDest = {};
      (this.suggestedMappings || []).forEach((row) => {
        if (row?.destination) {
          sourceByDest[row.destination] = row.source || '';
          maskByDest[row.destination] = normalizeColumnMask(row.mask);
        }
      });
      this.rebuildMappingRows(sourceByDest, [], maskByDest);
    },
    syncMappingFromNormalization(config) {
      if (!config) return;
      this.canonicalFields = config.canonical_fields || this.canonicalFields;
      // Prefer headers already loaded from /sourceHeaders. An empty
      // detected_source_headers array is truthy and would clear the dropdown.
      const liveHeaders = this.filterSourceHeaders(this.sourceHeaders);
      const configHeaders = this.filterSourceHeaders(config.detected_source_headers || []);
      this.sourceHeaders = liveHeaders.length ? liveHeaders : configHeaders;
      this.headerLabels = {
        ...(config.header_labels || {}),
        ...(this.headerLabels || {}),
      };
      const canonicalDests = new Set(
        (this.canonicalFields || []).map((field) => field.destination)
      );
      const sourceByDest = {};
      const maskByDest = {};
      const extras = [];
      (config.field_mappings || []).forEach((row) => {
        if (!row?.destination) return;
        if (canonicalDests.has(row.destination)) {
          sourceByDest[row.destination] = row.source || '';
          maskByDest[row.destination] = normalizeColumnMask(row.mask);
        } else if (row.source) {
          extras.push({
            destination: row.destination,
            source: row.source || '',
            mask: normalizeColumnMask(row.mask),
            required: !!row.required,
            isExtra: true,
            dataType: row.data_type || inferDataType(row.destination),
            displayLabel: row.display_label || '',
          });
        }
      });
      this.rebuildMappingRows(sourceByDest, extras, maskByDest);
      this.savedRows = cloneMappingRows(this.mappingRows);
      this.mappingDirty = false;
    },
    filterSourceHeaders(headers) {
      return (headers || []).filter(
        (slug) => slug && !SOURCE_HEADER_META_KEYS.has(String(slug))
      );
    },
    rebuildMappingRows(sourceByDest = {}, extras = [], maskByDest = {}) {
      const canonicalRows = (this.canonicalFields || []).map((field) => ({
        destination: field.destination,
        source: sourceByDest[field.destination] || '',
        mask: normalizeColumnMask(maskByDest[field.destination]),
        required: !!field.required,
        isExtra: false,
        dataType: field.data_type || inferDataType(field.destination),
      }));
      this.mappingRows = canonicalRows.concat(
        (extras || []).map((row) => this.createExtraRow(row))
      );
    },
    createExtraRow(row = {}) {
      return {
        rowKey: row.rowKey || this.nextExtraRowKey(),
        destination: row.destination || '',
        source: normalizeDropdownValue(row.source || row.erpColumn),
        mask: normalizeColumnMask(row.mask),
        required: !!row.required,
        isExtra: true,
        dataType: row.dataType || inferDataType(row.destination),
        displayLabel: row.displayLabel || '',
      };
    },
    nextExtraRowKey() {
      const key = `extra-row-${this.nextExtraRowKeyCounter}`;
      this.nextExtraRowKeyCounter += 1;
      return key;
    },
    async loadManagerCycles() {
      if (isAppSwitcherManagerUser(this.$store)) return;
      const cached = this.$store.state.m_financialclose?.cyclesForSwitcher;
      if (Array.isArray(cached) && cached.length) return;
      try {
        await this.$store.dispatch('fetchCyclesForSwitcher');
      } catch (err) {
        console.log('[DataConfigurationColumnMappingPanel]: loadManagerCycles', err);
      }
    },
    startEdit() {
      if (!this.canMutateMapping) return;
      this.savedRows = cloneMappingRows(this.mappingRows);
      this.mode = 'edit';
    },
    cancelEdit() {
      this.mappingRows = cloneMappingRows(this.savedRows);
      this.mappingDirty = false;
      this.mode = 'view';
    },
    markMappingDirty() {
      this.mappingDirty = true;
    },
    removeExtraColumn(index) {
      const row = this.mappingRows[index];
      if (!row || !row.isExtra) return;
      this.mappingRows.splice(index, 1);
      this.markMappingDirty();
      this.$store.commit(
        'sbSuccess',
        `Custom column "${row.destination}" removed. Click Save to persist.`,
        { root: true }
      );
    },
    editExtraColumn(row) {
      if (!row || !row.isExtra) return;
      this.editingRowKey = row.rowKey;
      this.showCustomColumnDialog = true;
    },
    editCanonicalColumn(row) {
      if (!row || row.isExtra || row.required) return;
      this.editingRowKey = row.destination;
      this.showCustomColumnDialog = true;
    },
    findRowByEditKey(key) {
      if (!key) return null;
      return (
        this.mappingRows.find(
          (entry) => (entry.isExtra && entry.rowKey === key) || (!entry.isExtra && entry.destination === key)
        ) || null
      );
    },
    unmapCanonicalColumn(row) {
      if (!row || row.isExtra || row.required || !row.source) return;
      row.source = '';
      this.markMappingDirty();
      this.$store.commit(
        'sbSuccess',
        `Mapping for "${row.destination}" removed. Click Save to persist.`,
        { root: true }
      );
    },
    onCustomColumnSave(payload) {
      const row = this.findRowByEditKey(payload?.rowKey);
      if (!row) return;
      row.source = normalizeDropdownValue(payload.erpColumn);
      row.displayLabel = payload.displayLabel || row.displayLabel;
      row.dataType = payload.dataType || row.dataType;
      row.mask = normalizeColumnMask(payload.mask);
      if (row.isExtra) row.required = !!payload.required;
      this.editingRowKey = null;
      this.markMappingDirty();
      this.$store.commit(
        'sbSuccess',
        `"${row.destination}" updated. Click Save to persist.`,
        { root: true }
      );
    },
    onCustomColumnCreate(payload) {
      const destination = slugifyColumnName(payload?.columnName);
      const erpColumn = normalizeDropdownValue(payload?.erpColumn);

      if (!destination) {
        this.$store.commit('sbError', 'Enter a valid column name.', { root: true });
        return;
      }
      if (!erpColumn) {
        this.$store.commit('sbError', 'Select an ERP column to map.', { root: true });
        return;
      }

      const exists = this.mappingRows.some((row) => row.destination === destination);
      if (exists) {
        this.$store.commit(
          'sbError',
          `Column "${destination}" already exists in the mapping table.`,
          { root: true }
        );
        return;
      }

      this.mappingRows.push(
        this.createExtraRow({
          destination,
          source: erpColumn,
          displayLabel: payload.displayLabel || destination,
          required: payload.required,
          dataType: payload.dataType,
          mask: payload.mask,
        })
      );
      this.markMappingDirty();
      this.$store.commit(
        'sbSuccess',
        `Custom column "${destination}" added. Click Save to persist.`,
        { root: true }
      );
    },
    buildFieldMappingsPayload() {
      return this.mappingRows
        .filter((row) => row.destination && row.source)
        .map((row) => {
          const mapping = {
            destination: row.destination,
            source: normalizeDropdownValue(row.source),
            required: !!row.required,
            mask: normalizeColumnMask(row.mask),
          };

          if (row.dataType) {
            mapping.data_type = row.dataType;
          }
          if (row.displayLabel) {
            mapping.display_label = row.displayLabel;
          }
          if (row.isExtra) {
            mapping.passthrough = true;
          }

          return mapping;
        });
    },
    async saveMapping() {
      if (!this.canMutateMapping || !this.canSave) return;
      await this.persistClientMapping();
    },
    async ingestNow() {
      if (!this.canMutateMapping || !this.canIngestNow) return;
      this.ingestPolling = true;
      this.ingestionStatusLoaded = true;
      this.ingestion = { status: 'in-progress', progress: 0 };
      this.syncIngestionPolling();
      try {
        await this.$store.dispatch('accruals/startIngestion', {
          projectId: '',
          cid: this.cid,
          entityType: this.entityType,
        });
        await this.refreshIngestionStatus();
      } catch (err) {
        this.ingestPolling = false;
        this.ingestion = { status: 'not_started', progress: 0 };
        this.stopIngestionPolling();
        console.log('[DataConfigurationColumnMappingPanel]: ingestNow', err);
      }
    },
    async persistClientMapping() {
      if (!this.canMutateMapping) return;
      this.startingIngestion = true;
      try {
        await this.$store.dispatch('accruals/saveNormalizationConfig', {
          projectId: this.projectId || '',
          cid: this.cid,
          entityType: this.entityType,
          sourceKind: 'file_upload',
          fieldMappings: this.buildFieldMappingsPayload(),
        });
        this.savedRows = cloneMappingRows(this.mappingRows);
        this.mappingDirty = false;
        this.mappingSaved = true;
        this.mode = 'view';
      } catch (err) {
        console.log('[DataConfigurationColumnMappingPanel]: saveMapping', err);
      } finally {
        this.startingIngestion = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./DataConfigurationColumnMappingPanel.scss"></style>
